import { SWRConfiguration } from "swr";

import { FetcherResult } from "../api-client/fetcher";
import { ModifiedSWRResponse, useSwrGet } from "../api-client/useSwrGet";
import { useAccesssoryCategories } from "../blocks/Accessories/Filters/Category/useAccesssoryCategories";
import { useCriteria } from "../blocks/Accessories/Filters/Criteria/useCriteria";
import { PartTypeAccessory } from "../constants/partTypes";
import { productTypeAccessory } from "../constants/productTypes";
import { LanguageMap, useLanguage } from "../i18n/useLanguage";
import { DealerVehicleResultsRoute } from "../navigation/DealerRoutes";
import { useRouteParams } from "../navigation/useRouteParams";
import { getPartName } from "../util/getPartName";
import { useGetVehicleInfo } from "./useGetVehicleInfo";
import { PartPricingItem } from "./useSearchTiresByVehicle";

export const useSearchAccessoriesByVehicle = (
  swrOptions: SWRConfiguration<FetcherResult<Payload>> = {}
): ModifiedSWRResponse<
  FetcherResult<Payload>,
  SearchAccessoriesByVehiclePayload
> => {
  const [language] = useLanguage();
  const { data: info } = useGetVehicleInfo();
  const route = useRouteParams(DealerVehicleResultsRoute);
  const shouldFetch =
    route?.productType === productTypeAccessory && Boolean(info?.vehicleId);
  const [categories] = useAccesssoryCategories();
  const [criteria] = useCriteria();

  const { data, ...result } = useSwrGet<Payload>(
    shouldFetch
      ? "/retailSiteWebService/parts/accessory/searchAccessoriesByVehicle.php"
      : null,
    {
      dealerId: route?.dealerId || "",
      isNational: "0",
      vehicleId: String(info?.vehicleId || ""),
    },
    swrOptions
  );

  if (!data) {
    return result as ModifiedSWRResponse<
      FetcherResult<Payload>,
      SearchAccessoriesByVehiclePayload
    >;
  }

  const categorySet = new Set(categories);

  const filteredParts = data.partList.filter((part) => {
    const category =
      categorySet.size > 0
        ? part.partDetails.primaryPart.accessoryCategories.some((category) => {
            const [key] = Object.keys(category);
            return categorySet.has(key);
          })
        : true;
    let matched = true;
    if (criteria.length) {
      const name = getPartName(part, language);
      matched = name.toLowerCase().includes(criteria.toLowerCase());
    }
    return matched && category && !part.partSummary.primaryPart.hideItem;
  });

  return { ...result, data: { ...data, filteredParts } };
};

type Payload = {
  partList: AccessoryPartItem[];
};

export type SearchAccessoriesByVehiclePayload = Payload & {
  filteredParts: AccessoryPartItem[];
};

export type AccessoryPartItem = {
  partDetails: {
    primaryPart: PrimaryAccessoryPart;
    relatedParts: PrimaryAccessoryPart[];
    secondaryParts: PrimaryAccessoryPart[];
  };
  partPricing: {
    primaryPart: AccessoryPartPricing;
    relatedParts: AccessoryPartPricing[];
    secondaryParts: AccessoryPartPricing[];
  };
  partStock: {
    primaryPart: AccessoryStockItem[];
    relatedParts: AccessoryStockItem[];
    secondaryParts: AccessoryStockItem[];
  };
  partSummary: {
    primaryPart: AccessoryPartSummary;
    relatedParts: AccessoryPartSummary[];
    secondaryParts: AccessoryPartSummary[];
  };
  partTire?: never;
  partVisualizerAsset: string[];
  partWheel?: never;
};

type AccessoryStockItem = {
  itemId: string;
  itemType: string;
  minutesSinceLastStockUpdate: number;
  optionId: string;
  sortOrder: string;
  stockApiForceRefreshOnLoad: boolean;
  stockApiQueryUrl: string;
  stockLocationId: string;
  stockLocationKey: string;
  stockLocationName: string;
  stockLocationType: string;
  stockLocationWebsite: string;
  stockQty: string;
  stockQtyLocal: string;
  stockQtyLocalPlus: string;
  stockQtyNationWide: string;
  tireLinkEnabled: boolean;
  tireStockApiQueryEngineCode: string;
};

export type AccessoryPartSummary = {
  hideItem: boolean;
  itemDescription: LanguageMap;
  itemHasDealerPricing: boolean;
  itemHasStock: boolean;
  itemId: string;
  itemImageLink: string;
  itemNationalPartNumber: string;
  itemOriginalEquipmentFlag: string;
  itemPartNumber: string;
  itemPartOption: null;
  itemShippingMetrics: null;
  itemTitle: LanguageMap;
  itemType: PartTypeAccessory;
  supportConsumerOrder: boolean;
  supportDealerOrder: boolean;
};

export type PrimaryAccessoryPart = {
  accessoryCategories: AccessoryCategoryEntry[];
  accessoryCategoryTags: string[];
  accessoryComment: string;
  accessoryDescription: LanguageMap;
  accessoryImageLink: string;
  accessoryManufacturerId: string;
  accessoryManufacturerName: string;
  accessoryName: LanguageMap;
  accessoryPartNumber: string;
  accessoryPopularityByVehicle: {
    [key: string]: {
      accessory_id: number;
      most_popular: string;
      popularity: string;
      vehicle_manufacturer_id: string;
      vehicle_model_id: string;
      vehicle_submodel_id: string;
      vehicle_year_id: string;
    };
  };
  accessoryRequiresInstallation: string;
  accessoryRestrictions: LanguageMap;
  isPackage: string;
  itemId: string;
  itemType: PartTypeAccessory;
};

export type AccessoryCategoryEntry = {
  [key: string]: LanguageMap & { sortOrder: string };
};

export type AccessoryCategory = LanguageMap & { sortOrder: string };

export type AccessoryPartPricing = {
  itemId: string;
  itemType: PartTypeAccessory;
  link: unknown[];
  price: {
    [key: string]: PartPricingItem;
  };
  priceLastUpdated: string;
  prioritizeInSearch: boolean;
};

export type ResultParameters = {
  dealerId: number;
  originalEquipmentFilter: string;
  season: number;
  vehicleId: number;
  vehicleSubmodelTireSizeId: number;
};
